import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
    ConfiguratorScenarioService,
    DatastoreDto,
    Partner,
    ScenarioDTO
} from "@tj/tj-dmp-lib-api";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { Router } from "@angular/router";

@Component({
    selector: "dmp-workflow",
    templateUrl: "./workflow.component.html",
    styleUrls: ["./workflow.component.scss"],
    standalone: false
})
export class WorkflowComponent implements OnInit {
    breadcrumb = [
        { label: this.translate.instant("menu.workflow.label") },
        { label: this.translate.instant("menu.workflow.settings") },
    ];
    description = this.translate.instant("workflow.list-page.description");

    datastore: DatastoreDto;
    partner: Partner;

    typeOptions = [
        {
            label: this.translate.instant("workflow.type.marketing"),
            value: ScenarioDTO.TypeEnum.MARKETING,
        },
        {
            label: this.translate.instant("workflow.type.transactional"),
            value: ScenarioDTO.TypeEnum.TRANSACTIONAL,
        },
    ];
    filterAndStatusOptions = [
        {
            label: this.translate.instant("workflow.list-page.filter-status.all"),
            value: null,
        },
        {
            label: this.translate.instant("workflow.list-page.filter-status.active"),
            value: ScenarioDTO.StatusScenarioEnum.INPROGRESS,
        },
        {
            label: this.translate.instant("workflow.list-page.filter-status.paused"),
            value: ScenarioDTO.StatusScenarioEnum.ONPAUSE,
        },
        {
            label: this.translate.instant("workflow.list-page.filter-status.stopped"),
            value: ScenarioDTO.StatusScenarioEnum.STOPPED,
        },
        {
            label: this.translate.instant("workflow.list-page.filter-status.archived"),
            value: ScenarioDTO.StatusScenarioEnum.ARCHIVE,
        },
    ];
    // null: every scenario except the archived ones, which have their own filter.
    selectedFilter: ScenarioDTO.StatusScenarioEnum = null;
    // The status filter, each option with its number of scenarios.
    statusOptions: { label: string; value: ScenarioDTO.StatusScenarioEnum }[] = [];

    scenarios: ScenarioDTO[] = [];
    displayData: ScenarioDTO[] = [];
    selectedScenarios: ScenarioDTO[] = [];
    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];

    @ViewChild('scenarioFileInput') scenarioFileInput: ElementRef;

    isCreatingWorkflow: boolean = false;

    constructor(
        private configuratorScenarioService: ConfiguratorScenarioService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private router: Router,
        private translate: TranslateService
    ) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            { label: this.translate.instant("menu.workflow.label") },
            { label: this.translate.instant("menu.workflow.settings") },
        ];
        this.description = this.translate.instant("workflow.list-page.description");
    }

    ngOnInit(): void {
        this.initExportMenu();
        this.updateStatusOptions();
    }

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        // The selection is exported from the bar above the table, where the number of selected
        // scenarios is shown.
        this.exportMenuItems = [
            {
                label: this.translate.instant('workflow.list-page.menu.import'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.translate.instant('workflow.list-page.menu.exportDisplayed'),
                icon: 'fas fa-download',
                command: () => this.exportAllScenarios(),
                disabled: true
            }
        ];
    }

    datastoreChange(datastore: DatastoreDto) {
        this.datastore = datastore;
        this.loadAllData();
    }

    partnerChange(partner: Partner) {
        this.partner = partner;
        this.loadAllData();
    }

    loadAllData() {
        if (!this.datastore || !this.partner) {
            this.scenarios = [];
            this.filterStatus();
            return;
        }
        this.configuratorScenarioService
            .listOrLatest(this.datastore.code, this.partner.code)
            .subscribe(scenarios => {
                // Filter scenarios by affectedPartners
                const filteredScenarios = scenarios.filter(scenario =>
                    scenario.affectedPartners && scenario.affectedPartners.includes(this.partner.code)
                );
                this.scenarios = filteredScenarios.sort((a, b) => {
                    return a.name.localeCompare(b.name);
                });
                this.updateStatusOptions();
                this.filterStatus();
            });
    }

    search = '';
    first = 0;

    onSearch(value: string) {
        this.search = value;
        this.updateStatusOptions();
        this.filterStatus();
    }

    private matchesSearch(item: ScenarioDTO): boolean {
        const search = this.search?.trim().toLowerCase();
        return !search || [item.name, item.description].some(value => value?.toLowerCase().includes(search));
    }

    filterStatus() {
        this.displayData = (this.scenarios ?? [])
            .filter(item => this.matchesFilter(item, this.selectedFilter) && this.matchesSearch(item));
        this.first = 0;
        // A selection is made on what is displayed: kept across a filter change, it would export
        // scenarios that are no longer shown.
        this.clearSelection();
        this.exportMenuItems[1].disabled = this.displayData.length === 0;
    }

    private matchesFilter(item: ScenarioDTO, filter: ScenarioDTO.StatusScenarioEnum): boolean {
        return filter === null
            ? item.statusScenario !== ScenarioDTO.StatusScenarioEnum.ARCHIVE
            : item.statusScenario === filter;
    }

    private updateStatusOptions(): void {
        this.statusOptions = this.filterAndStatusOptions.map(option => ({
            label: `${option.label} (${this.scenarios.filter(item => this.matchesFilter(item, option.value) && this.matchesSearch(item)).length})`,
            value: option.value
        }));
    }

    clearSelection() {
        this.selectedScenarios = [];
    }

    // Same colors as the status in the scenario details: green when running, red when stopped.
    statusSeverity(status: ScenarioDTO.StatusScenarioEnum): 'success' | 'warn' | 'danger' | 'secondary' {
        switch (status) {
            case ScenarioDTO.StatusScenarioEnum.INPROGRESS:
                return 'success';
            case ScenarioDTO.StatusScenarioEnum.ONPAUSE:
                return 'warn';
            case ScenarioDTO.StatusScenarioEnum.STOPPED:
                return 'danger';
            default:
                return 'secondary';
        }
    }

    getLabelFromValue(list: { label: string; value: string }[], value: string): string {
        const found = list.find((item) => item.value === value);
        return found ? found.label : value;
    }

    // "Statut" reflète l'état de la version publiée (latestPublishedVersion) : c'est elle qui est
    // affichée en premier. Un brouillon plus récent est signalé à côté, sans passer pour ce qui tourne.
    draftVersion(data: ScenarioDTO): number | null {
        const published = data.latestPublishedVersion;
        if (published == null) {
            return data.statusVersion === ScenarioDTO.StatusVersionEnum.DRAFT ? data.version : null;
        }
        return data.version != null && data.version > published ? data.version : null;
    }

    openCreationDialog() {
        this.isCreatingWorkflow = true;
    }

    viewWorkflow(data: ScenarioDTO) {
        this.router.navigate(['/h/workflow', this.datastore.code, data.scenarioId, 'view'], {
            queryParams: { version: data.version }
        });
    }

    editWorkflow(data: ScenarioDTO) {
        // data.version est la dernière version (peut être un brouillon plus récent que la version
        // publiée, ex. "2 (v3 brouillon)") : c'est elle qui sera ouverte, pas forcément la publiée.
        // Un brouillon ne fork jamais (voir workflow-details.onEdit/willCreateNewVersion), l'avertir
        // ici serait donc trompeur.
        const editingPublishedVersion = data.statusVersion === ScenarioDTO.StatusVersionEnum.PUBLISH;
        if (data.statusScenario !== ScenarioDTO.StatusScenarioEnum.STOPPED && editingPublishedVersion) {
            this.confirmationService.confirm({
                message: this.translate.instant("workflow.confirmation.edit-running"),
                ...this.confirmButtons('edit', 'fas fa-pen'),
                accept: () => {
                    this.router.navigate(['/h/workflow', this.datastore.code, data.scenarioId, 'edit'], {
                        queryParams: { version: data.version }
                    });
                },
            });
        } else {
            this.router.navigate(['/h/workflow', this.datastore.code, data.scenarioId, 'edit'], {
                queryParams: { version: data.version }
            });
        }
    }

    // The confirmation names the action ("Archiver") rather than answering "Oui".
    private confirmButtons(action: string, icon: string) {
        return {
            acceptLabel: this.translate.instant(`workflow.list-page.confirm.${action}`),
            acceptIcon: icon,
            rejectLabel: this.translate.instant('workflow.list-page.confirm.cancel')
        };
    }

    archiveWorkflow(data: ScenarioDTO) {
        if (data.statusScenario !== ScenarioDTO.StatusScenarioEnum.STOPPED) {
            this.messageService.add({
                severity: "error",
                summary: this.translate.instant("workflow.message.error"),
                detail: this.translate.instant("workflow.error.currently-running"),
            });
        } else {
            this.confirmationService.confirm({
                message: this.translate.instant("workflow.confirmation.archive"),
                ...this.confirmButtons('archive', 'fas fa-box'),
                accept: () => {
                    this.performArchive(data);
                },
            });
        }
    }

    performArchive(data: ScenarioDTO) {
        this.configuratorScenarioService.updateStatus(this.datastore.code, this.partner.code, data.scenarioId, data.version, ScenarioDTO.StatusScenarioEnum.ARCHIVE).subscribe(() => {
            this.messageService.add({
                severity: "success",
                summary: this.translate.instant("workflow.message.success"),
                detail: this.translate.instant("workflow.message.archived"),
            });
            this.loadAllData();
        }, () => {
            this.messageService.add({
                severity: "error",
                summary: this.translate.instant("workflow.message.error"),
                detail: this.translate.instant("workflow.error.not-archived"),
            });
        });
    }

    restoreWorkflow(data: ScenarioDTO) {
        if (data.statusScenario !== ScenarioDTO.StatusScenarioEnum.ARCHIVE) {
            this.messageService.add({
                severity: "error",
                summary: this.translate.instant("workflow.message.error"),
                detail: this.translate.instant("workflow.error.archived"),
            });
        } else {
            this.confirmationService.confirm({
                message: this.translate.instant("workflow.confirmation.restore"),
                ...this.confirmButtons('restore', 'fas fa-undo'),
                accept: () => {
                    this.performRestore(data);
                },
            });
        }
    }

    performRestore(data: ScenarioDTO) {
        this.configuratorScenarioService.updateStatus(this.datastore.code, this.partner.code, data.scenarioId, data.version, ScenarioDTO.StatusScenarioEnum.STOPPED).subscribe(() => {
            this.messageService.add({
                severity: "success",
                summary: this.translate.instant("workflow.message.success"),
                detail: this.translate.instant("workflow.message.restored"),
            });
            this.loadAllData();
        }, () => {
            this.messageService.add({
                severity: "error",
                summary: this.translate.instant("workflow.message.error"),
                detail: this.translate.instant("workflow.error.not-restored"),
            });
        });
    }

    removeWorkflow(data: ScenarioDTO) {
        if (data.statusScenario !== ScenarioDTO.StatusScenarioEnum.ARCHIVE) {
            this.messageService.add({
                severity: "error",
                summary: this.translate.instant("workflow.message.error"),
                detail: this.translate.instant("workflow.error.not-archived"),
            });
        } else {
            this.confirmationService.confirm({
                message: this.translate.instant("workflow.confirmation.remove"),
                ...this.confirmButtons('remove', 'fas fa-trash'),
                accept: () => {
                    this.performRemove(data);
                },
            });
        }
    }

    performRemove(data: ScenarioDTO) {
        this.configuratorScenarioService.deleteScenario(this.datastore.code, this.partner.code, data.scenarioId).subscribe(() => {
            this.messageService.add({
                severity: "success",
                summary: this.translate.instant("workflow.message.success"),
                detail: this.translate.instant("workflow.message.removed"),
            });
            this.loadAllData();
        }, (error) => {
            const key = error?.status === 409
                ? "workflow.error.not-removed-linked-campaign"
                : "workflow.error.not-removed";
            this.messageService.add({
                severity: "error",
                summary: this.translate.instant("workflow.message.error"),
                detail: this.translate.instant(key),
            });
        });
    }

    // ============ EXPORT/IMPORT METHODS ============

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.scenarioFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('workflow.message.error'),
                detail: this.translate.instant('workflow.list-page.toast.unsupportedFormat')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.configuratorScenarioService.importScenarioJson(this.datastore.code, jsonContent).subscribe(
                (scenario: ScenarioDTO) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('workflow.message.success'),
                        detail: this.translate.instant('workflow.list-page.toast.importOne', {name: scenario.name})
                    });
                    this.loadAllData();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('workflow.message.error'),
                        detail: error.error?.detail || this.translate.instant('workflow.list-page.toast.importError')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.configuratorScenarioService.importScenariosZip(this.datastore.code, file).subscribe(
            (scenarios: ScenarioDTO[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('workflow.message.success'),
                    detail: this.translate.instant('workflow.list-page.toast.importMany', {count: scenarios.length})
                });
                this.loadAllData();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('workflow.message.error'),
                    detail: error.error?.detail || this.translate.instant('workflow.list-page.toast.importError')
                });
            }
        );
    }

    /**
     * Exporte un seul scénario en JSON
     */
    exportScenarioJson(scenario: ScenarioDTO) {
        this.configuratorScenarioService.exportScenarioJson(this.datastore.code, scenario.scenarioId, scenario.version).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `scenario-${scenario.name}-v${scenario.version}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('workflow.message.success'),
                    detail: this.translate.instant('workflow.list-page.toast.exportOne')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('workflow.message.error'),
                    detail: this.translate.instant('workflow.list-page.toast.exportError')
                });
            }
        );
    }

    /**
     * Exporte plusieurs scénarios en ZIP
     */
    exportSelectedScenarios() {
        const scenarioIds = this.selectedScenarios.map(s => s.scenarioId);
        const versions = this.selectedScenarios.map(s => s.version);
        this.configuratorScenarioService.exportScenariosZip(this.datastore.code, scenarioIds, versions).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `scenarios-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('workflow.message.success'),
                    detail: this.translate.instant('workflow.list-page.toast.exportMany', {count: scenarioIds.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('workflow.message.error'),
                    detail: this.translate.instant('workflow.list-page.toast.exportError')
                });
            }
        );
    }

    /**
     * Exporte tous les scénarios filtrés en ZIP
     */
    exportAllScenarios() {
        if (!this.displayData || this.displayData.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('workflow.message.error'),
                detail: this.translate.instant('workflow.list-page.toast.nothingToExport')
            });
            return;
        }

        const scenarioIds = this.displayData.map(s => s.scenarioId);
        const versions = this.displayData.map(s => s.version);
        this.configuratorScenarioService.exportScenariosZip(this.datastore.code, scenarioIds, versions).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `scenarios-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('workflow.message.success'),
                    detail: this.translate.instant('workflow.list-page.toast.exportMany', {count: scenarioIds.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('workflow.message.error'),
                    detail: this.translate.instant('workflow.list-page.toast.exportError')
                });
            }
        );
    }

}
